import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type CarrierAdmin, type CarrierCheck } from "@/api";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
const carriersKey = ["delivery", "carriers"] as const;
const statusVariant = (s: CarrierAdmin["status"]) => s === "active" ? "default" : s === "no_keys" ? "destructive" : "secondary";

// «Перевозчики» в настройках доставки: какие включены, подключены ли (ключи),
// что умеют и куда возят. Ключи задаются в env шлюза — консоль показывает
// только имена недостающих переменных и честно говорит «нет ключей».
export function DeliveryCarriers() {
  const $ = _c(37);
  const {
    t
  } = useTranslation();
  const qc = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: carriersKey,
      queryFn: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    error,
    isPending
  } = useQuery(t0);
  let t1;
  if ($[1] !== qc) {
    t1 = c => qc.setQueryData(carriersKey, prev => prev?.map(x => x.id === c.id ? c : x));
    $[1] = qc;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const put = t1;
  let t2;
  if ($[3] !== put) {
    t2 = {
      mutationFn: _temp2,
      onSuccess: put
    };
    $[3] = put;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggle = useMutation(t2);
  if (error) {
    let t3;
    if ($[5] !== error.message) {
      t3 = <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error.message}</div>;
      $[5] = error.message;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  if (isPending || !data) {
    let t3;
    if ($[7] !== t) {
      t3 = t("carriers.loading");
      $[7] = t;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== t3) {
      t4 = <p className="text-muted-foreground">{t3}</p>;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t3;
  if ($[11] !== t) {
    t3 = t("carriers.title");
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t3) {
    t4 = <h2 className="text-lg font-semibold">{t3}</h2>;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t) {
    t5 = t("carriers.intro");
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <div className="flex flex-col gap-1" data-tour="carriers-intro">{t4}{t6}</div>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== toggle.error) {
    t8 = toggle.error && <p className="text-sm text-destructive">{(toggle.error as Error).message}</p>;
    $[22] = toggle.error;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== data || $[25] !== put || $[26] !== toggle) {
    let t10;
    if ($[28] !== put || $[29] !== toggle) {
      t10 = c_1 => <CarrierCard key={c_1.id} carrier={c_1} onToggle={() => toggle.mutate(c_1)} toggling={toggle.isPending && toggle.variables?.id === c_1.id} onUpdated={put} />;
      $[28] = put;
      $[29] = toggle;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t9 = data.map(t10);
    $[24] = data;
    $[25] = put;
    $[26] = toggle;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[31] !== t9) {
    t10 = <div className="grid gap-4 lg:grid-cols-3" data-tour="carriers-list">{t9}</div>;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t7 || $[35] !== t8) {
    t11 = <section className="flex flex-col gap-4">{t7}{t8}{t10}</section>;
    $[33] = t10;
    $[34] = t7;
    $[35] = t8;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp2(c_0) {
  return api.deliveryCarriers.setEnabled(c_0.id, !c_0.enabled);
}
function _temp() {
  return api.deliveryCarriers.list();
}
function CarrierCard(t0) {
  const $ = _c(77);
  const {
    carrier: c,
    onToggle,
    toggling,
    onUpdated
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== c.capabilities) {
    t1 = Object.keys(c.capabilities);
    $[0] = c.capabilities;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1 as (keyof CarrierAdmin["capabilities"])[];
  let t3;
  if ($[2] !== c.capabilities || $[3] !== t2) {
    t3 = t2.filter(k => c.capabilities[k]);
    $[2] = c.capabilities;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const caps = t3;
  const t4 = `carrier-${c.id}`;
  let t5;
  if ($[5] !== c.title) {
    t5 = <h3 className="text-base font-semibold">{c.title}</h3>;
    $[5] = c.title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== c.status) {
    t6 = statusVariant(c.status);
    $[7] = c.status;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `carriers.status.${c.status}`;
  let t8;
  if ($[9] !== t || $[10] !== t7) {
    t8 = t(t7);
    $[9] = t;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t8) {
    t9 = <Badge variant={t6}>{t8}</Badge>;
    $[12] = t6;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t9) {
    t10 = <div className="flex flex-col gap-1">{t5}{t9}</div>;
    $[15] = t5;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = c.enabled ? "default" : "outline";
  let t12;
  if ($[18] !== c.enabled || $[19] !== t) {
    t12 = c.enabled ? t("carriers.enabled") : t("carriers.disabled");
    $[18] = c.enabled;
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== c.enabled || $[22] !== onToggle || $[23] !== t11 || $[24] !== t12 || $[25] !== toggling) {
    t13 = <Button variant={t11} size="sm" onClick={onToggle} disabled={toggling} aria-pressed={c.enabled} data-tour="carriers-toggle">{t12}</Button>;
    $[21] = c.enabled;
    $[22] = onToggle;
    $[23] = t11;
    $[24] = t12;
    $[25] = toggling;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13) {
    t14 = <header className="flex items-start justify-between gap-3">{t10}{t13}</header>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== c.enabled || $[31] !== c.missing || $[32] !== c.status || $[33] !== t) {
    t15 = c.status === "no_keys" && <div className="flex flex-col gap-1 rounded-md bg-destructive/5 p-3 text-sm"><p>{c.enabled ? t("carriers.noKeysEnabled") : t("carriers.noKeys")}</p><p className="font-mono text-xs text-muted-foreground">{c.missing.join(", ")}</p></div>;
    $[30] = c.enabled;
    $[31] = c.missing;
    $[32] = c.status;
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== c.status || $[36] !== t) {
    t16 = c.status === "disabled" && <p className="text-sm text-muted-foreground">{t("carriers.disabledHint")}</p>;
    $[35] = c.status;
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("carriers.capabilitiesHeading");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <span className="text-xs font-semibold uppercase text-muted-foreground">{t17}</span>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== caps || $[43] !== t) {
    t19 = <div className="flex flex-wrap gap-1.5">{caps.map(k_0 => <Badge key={k_0} variant="outline">{t(`carriers.capabilities.${k_0}`)}</Badge>)}</div>;
    $[42] = caps;
    $[43] = t;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t18 || $[46] !== t19) {
    t20 = <div className="flex flex-col gap-1.5">{t18}{t19}</div>;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t) {
    t21 = t("carriers.coverageHeading");
    $[48] = t;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t21) {
    t22 = <span className="text-xs font-semibold uppercase text-muted-foreground">{t21}</span>;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== c.coverage.note) {
    t23 = <p className="text-sm">{c.coverage.note}</p>;
    $[52] = c.coverage.note;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t22 || $[55] !== t23) {
    t24 = <div className="flex flex-col gap-1.5">{t22}{t23}</div>;
    $[54] = t22;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== c || $[58] !== onUpdated) {
    t25 = <CourierZones carrier={c} onUpdated={onUpdated} />;
    $[57] = c;
    $[58] = onUpdated;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== c) {
    t26 = <CoverageCheck carrier={c} />;
    $[60] = c;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  const t27 = c.baseUrl;
  let t28;
  if ($[62] !== t) {
    t28 = t("carriers.baseUrl");
    $[62] = t;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== c.baseUrl || $[65] !== t28) {
    t29 = <p className="truncate text-xs text-muted-foreground" title={t27}>{t28}: {c.baseUrl}</p>;
    $[64] = c.baseUrl;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t14 || $[68] !== t15 || $[69] !== t16 || $[70] !== t20 || $[71] !== t24 || $[72] !== t25 || $[73] !== t26 || $[74] !== t29 || $[75] !== t4) {
    t30 = <article className="flex flex-col gap-4 rounded-lg border p-5" data-testid={t4}>{t14}{t15}{t16}{t20}{t24}{t25}{t26}{t29}</article>;
    $[67] = t14;
    $[68] = t15;
    $[69] = t16;
    $[70] = t20;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
    $[74] = t29;
    $[75] = t4;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  return t30;
}
function CourierZones(t0) {
  const $ = _c(55);
  const {
    carrier: c,
    onUpdated
  } = t0;
  const {
    t
  } = useTranslation();
  const [city, setCity] = useState("");
  let t1;
  if ($[0] !== c.id || $[1] !== city) {
    t1 = () => api.deliveryCarriers.upsertZone(c.id, city.trim(), false);
    $[0] = c.id;
    $[1] = city;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onUpdated) {
    t2 = next => {
      setCity("");
      onUpdated(next);
    };
    $[3] = onUpdated;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const add = useMutation(t3);
  let t4;
  if ($[8] !== c.id) {
    t4 = zoneId => api.deliveryCarriers.removeZone(c.id, zoneId);
    $[8] = c.id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onUpdated || $[11] !== t4) {
    t5 = {
      mutationFn: t4,
      onSuccess: onUpdated
    };
    $[10] = onUpdated;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const remove = useMutation(t5);
  if (!c.capabilities.courier) {
    return null;
  }
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[13] !== c.coverage.courierZones || $[14] !== remove || $[15] !== t) {
    const blocked = c.coverage.courierZones.filter(_temp3);
    t6 = "flex flex-col gap-2";
    t7 = "carriers-zones";
    let t10;
    if ($[20] !== t) {
      t10 = t("carriers.zonesHeading");
      $[20] = t;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    if ($[22] !== t10) {
      t8 = <span className="text-xs font-semibold uppercase text-muted-foreground">{t10}</span>;
      $[22] = t10;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t9 = blocked.length === 0 ? <p className="text-sm text-muted-foreground">{t("carriers.zonesEmpty")}</p> : <div className="flex flex-wrap gap-1.5">{blocked.map(z_0 => <Badge key={z_0.id} variant="secondary" className="gap-1.5">{z_0.city}<button type="button" className="text-muted-foreground hover:text-foreground" aria-label={t("carriers.zoneRemove", {
          city: z_0.city
        })} onClick={() => remove.mutate(z_0.id)}>×</button></Badge>)}</div>;
    $[13] = c.coverage.courierZones;
    $[14] = remove;
    $[15] = t;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t10;
  if ($[24] !== add || $[25] !== city) {
    t10 = e => {
      e.preventDefault();
      if (city.trim()) {
        add.mutate();
      }
    };
    $[24] = add;
    $[25] = city;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => setCity(e_0.target.value);
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t) {
    t12 = t("carriers.zonePlaceholder");
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== city || $[31] !== t12) {
    t13 = <Input value={city} onChange={t11} placeholder={t12} className="h-8" />;
    $[30] = city;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== add.isPending || $[34] !== city) {
    t14 = !city.trim() || add.isPending;
    $[33] = add.isPending;
    $[34] = city;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("carriers.zoneAdd");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t14 || $[39] !== t15) {
    t16 = <Button type="submit" size="sm" variant="outline" disabled={t14}>{t15}</Button>;
    $[38] = t14;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t10 || $[42] !== t13 || $[43] !== t16) {
    t17 = <form className="flex gap-2" onSubmit={t10}>{t13}{t16}</form>;
    $[41] = t10;
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== add.error || $[46] !== remove) {
    t18 = (add.error || remove.error) && <p className="text-xs text-destructive">{((add.error || remove.error) as Error).message}</p>;
    $[45] = add.error;
    $[46] = remove;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t17 || $[49] !== t18 || $[50] !== t6 || $[51] !== t7 || $[52] !== t8 || $[53] !== t9) {
    t19 = <div className={t6} data-tour={t7}>{t8}{t9}{t17}{t18}</div>;
    $[48] = t17;
    $[49] = t18;
    $[50] = t6;
    $[51] = t7;
    $[52] = t8;
    $[53] = t9;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
}
function _temp3(z) {
  return !z.enabled;
}
const destinationKey = (d: CarrierCheck["rates"][number]["destination"]) => `carriers.destination.${d}`;
function CoverageCheck(t0) {
  const $ = _c(61);
  const {
    carrier: c
  } = t0;
  const {
    t
  } = useTranslation();
  const [city, setCity] = useState("");
  const [cargo, setCargo] = useState(c.capabilities.heavyFreight ? "heavy" : "parcel");
  let t1;
  if ($[0] !== c.id || $[1] !== cargo || $[2] !== city) {
    t1 = {
      mutationFn: () => api.deliveryCarriers.check(c.id, {
        city: city.trim(),
        cargo
      })
    };
    $[0] = c.id;
    $[1] = cargo;
    $[2] = city;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const check = useMutation(t1);
  const r = check.data;
  let t2;
  if ($[4] !== t) {
    t2 = t("carriers.checkHeading");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <span className="text-xs font-semibold uppercase text-muted-foreground">{t2}</span>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== check || $[9] !== city) {
    t4 = e => {
      e.preventDefault();
      if (city.trim()) {
        check.mutate();
      }
    };
    $[8] = check;
    $[9] = city;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => setCity(e_0.target.value);
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("carriers.checkPlaceholder");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("carriers.checkPlaceholder");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== city || $[17] !== t6 || $[18] !== t7) {
    t8 = <Input value={city} onChange={t5} placeholder={t6} className="h-8 min-w-0 flex-1" aria-label={t7} />;
    $[16] = city;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = e_1 => setCargo(e_1.target.value as "parcel" | "heavy");
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t) {
    t10 = t("carriers.cargoLabel");
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("carriers.cargo.parcel");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <option value="parcel">{t11}</option>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("carriers.cargo.heavy");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13) {
    t14 = <option value="heavy">{t13}</option>;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== cargo || $[32] !== t10 || $[33] !== t12 || $[34] !== t14) {
    t15 = <select value={cargo} onChange={t9} className="h-8 rounded-md border bg-background px-2 text-sm" aria-label={t10}>{t12}{t14}</select>;
    $[31] = cargo;
    $[32] = t10;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== check.isPending || $[37] !== city) {
    t16 = !city.trim() || check.isPending;
    $[36] = check.isPending;
    $[37] = city;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== check.isPending || $[40] !== t) {
    t17 = check.isPending ? t("carriers.checking") : t("carriers.check");
    $[39] = check.isPending;
    $[40] = t;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t16 || $[43] !== t17) {
    t18 = <Button type="submit" size="sm" disabled={t16}>{t17}</Button>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t15 || $[46] !== t18 || $[47] !== t4 || $[48] !== t8) {
    t19 = <form className="flex flex-wrap gap-2" onSubmit={t4}>{t8}{t15}{t18}</form>;
    $[45] = t15;
    $[46] = t18;
    $[47] = t4;
    $[48] = t8;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== check.error) {
    t20 = check.error && <p className="text-xs text-destructive">{(check.error as Error).message}</p>;
    $[50] = check.error;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== c.id || $[53] !== r || $[54] !== t) {
    t21 = r && <div className="flex flex-col gap-1.5 text-sm" data-testid={`carrier-check-${c.id}`}>{r.reason === "no_keys" ? <p className="text-destructive">{t("carriers.checkNoKeys")}</p> : r.reason === "carrier_error" ? <p className="text-destructive">{t("carriers.checkError")}: {r.error}</p> : r.rates.length === 0 ? <p className="text-destructive">{t("carriers.checkEmpty", {
          city: r.city
        })}</p> : <><p className="text-muted-foreground">{t("carriers.checkSummary", {
            city: r.city,
            points: r.points
          })}{r.reason === "disabled" ? ` ${t("carriers.checkDisabled")}` : ""}</p><ul className="flex flex-col divide-y rounded-md border">{r.rates.slice(0, 6).map(rate => <li key={`${rate.serviceCode}-${rate.destination}`} className="flex items-center justify-between gap-3 px-3 py-1.5"><span className="min-w-0"><span className="block truncate">{rate.name}</span><span className="text-xs text-muted-foreground">{t(destinationKey(rate.destination))} ·{" "}{rate.daysMin === rate.daysMax ? t("carriers.days", {
                  count: rate.daysMax
                }) : t("carriers.daysRange", {
                  min: rate.daysMin,
                  max: rate.daysMax
                })}</span></span><span className="shrink-0 font-medium tabular-nums">{rate.price.toLocaleString("ru-RU")} ₽</span></li>)}</ul></>}</div>;
    $[52] = c.id;
    $[53] = r;
    $[54] = t;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t19 || $[57] !== t20 || $[58] !== t21 || $[59] !== t3) {
    t22 = <div className="flex flex-col gap-2 border-t pt-3" data-tour="carriers-check">{t3}{t19}{t20}{t21}</div>;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t3;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  return t22;
}